/**
 * On-demand loaders for the YouTube and Vimeo player APIs, used to watch
 * playback progress inside their iframes. Nothing here runs until a viewer
 * presses play, and every failure is swallowed by the caller — tracking is
 * best-effort, the video itself never depends on it.
 */

export interface YouTubePlayer {
    getCurrentTime(): number;
    getDuration(): number;
}

interface YouTubeApi {
    Player: new (
        el: HTMLIFrameElement,
        opts: {
            host?: string;
            events?: {
                onReady?: () => void;
                onStateChange?: (e: { data: number }) => void;
            };
        }
    ) => YouTubePlayer;
    PlayerState: { ENDED: number };
}

export interface VimeoPlayer {
    on(event: 'timeupdate', cb: (data: { percent: number }) => void): void;
    on(event: 'ended', cb: () => void): void;
    off(event: 'timeupdate' | 'ended'): void;
}

interface VimeoApi {
    Player: new (el: HTMLIFrameElement) => VimeoPlayer;
}

interface ApiWindow extends Window {
    YT?: YouTubeApi;
    Vimeo?: VimeoApi;
    onYouTubeIframeAPIReady?: () => void;
}

const w = () => window as ApiWindow;

const scripts = new Map<string, Promise<void>>();

function loadScript(src: string): Promise<void> {
    const existing = scripts.get(src);
    if (existing) return existing;

    const promise = new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => {
            scripts.delete(src); // allow a retry on the next play
            script.remove();
            reject(new Error(`Failed to load ${src}`));
        };
        document.head.appendChild(script);
    });
    scripts.set(src, promise);
    return promise;
}

let youTubeReady: Promise<YouTubeApi> | null = null;

/** Resolves once `YT.Player` is usable — the script alone isn't enough, it loads a second one. */
export function loadYouTubeApi(): Promise<YouTubeApi> {
    const ready = w().YT;
    if (ready?.Player) return Promise.resolve(ready);
    if (youTubeReady) return youTubeReady;

    youTubeReady = new Promise<YouTubeApi>((resolve, reject) => {
        const previous = w().onYouTubeIframeAPIReady;
        w().onYouTubeIframeAPIReady = () => {
            previous?.();
            resolve(w().YT as YouTubeApi);
        };
        loadScript('https://www.youtube.com/iframe_api').catch((err) => {
            youTubeReady = null;
            reject(err);
        });
    });
    return youTubeReady;
}

export function loadVimeoApi(): Promise<VimeoApi> {
    return loadScript('https://player.vimeo.com/api/player.js').then(() => {
        const api = w().Vimeo;
        if (!api) throw new Error('Vimeo player API unavailable');
        return api;
    });
}
